import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ManagerLayout from "../../layouts/ManagerLayout";
import * as XLSX from "xlsx";
import { createProject, getProjectLocations } from "../../api/projectService";

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: "bg-white/10",        text: "text-white/50",   label: "Pending"  },
    success:  { bg: "bg-green-500/20",     text: "text-green-300",  label: "✓ Created" },
    error:    { bg: "bg-red-500/20",       text: "text-red-300",    label: "✗ Failed"  },
    skipped:  { bg: "bg-yellow-500/20",    text: "text-yellow-300", label: "— Skipped" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

const REQUIRED_COLS = ["project_id", "project_name", "project_description", "location"];

export default function BulkCreateProjects() {
  const navigate     = useNavigate();

  const [rows, setRows]             = useState([]);
  const [parseError, setParseError] = useState("");
  const [uploading, setUploading]   = useState(false);
  const [results, setResults]       = useState([]);
  const [done, setDone]             = useState(false);
  const [locMap, setLocMap]         = useState({});
  const [locations, setLocations]   = useState([]);

  useEffect(() => {
    getProjectLocations()
      .then((res) => {
        const locs = res.data.locations || [];
        setLocations(locs);
        const map = {};
        locs.forEach((l) => { map[l.name.toLowerCase()] = l.id; });
        setLocMap(map);
      })
      .catch(() => {});
  }, []);

  const handleDownloadTemplate = () => {
    const wb   = XLSX.utils.book_new();
    const data = [
      REQUIRED_COLS,
      ["PROJ-001", "Website Redesign",   "Full redesign of corporate website.", "Hyderabad Office"],
      ["PROJ-002", "Mobile App v2",      "Cross-platform React Native app.",    "Mumbai Office"],
      ["PROJ-003", "Data Pipeline ETL",  "Migrate legacy SQL to Snowflake.",    "Delhi Office"],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws["!cols"] = [{ wch: 18 }, { wch: 30 }, { wch: 50 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, ws, "projects_bulk");
    XLSX.writeFile(wb, "bulk_projects_template.xlsx");
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(""); setRows([]); setResults([]); setDone(false);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb  = XLSX.read(evt.target.result, { type: "array" });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!raw.length) { setParseError("File is empty or unreadable."); return; }

        const missing = REQUIRED_COLS.filter((c) => !(c in raw[0]));
        if (missing.length) {
          setParseError(`Missing required columns: ${missing.join(", ")}. Make sure you are using the correct template.`);
          return;
        }

        const parsed = raw
          .map((r, i) => ({
            _idx:                i + 1,
            project_id:          String(r.project_id || "").trim().toUpperCase(),
            project_name:        String(r.project_name || "").trim(),
            project_description: String(r.project_description || "").trim(),
            location_name:       String(r.location || "").trim(),
          }))
          .filter((r) => r.project_id || r.project_name);

        if (!parsed.length) { setParseError("No data rows found."); return; }

        const seen = new Set();
        const withMeta = parsed.map((r) => {
          let rowError = "";
          if (!r.project_id)               rowError = "project_id is required";
          else if (!r.project_name)        rowError = "project_name is required";
          else if (!r.project_description) rowError = "project_description is required";
          else if (!r.location_name)       rowError = "location is required";
          else if (!locMap[r.location_name.toLowerCase()])
                                           rowError = `Location "${r.location_name}" not found in system`;
          else if (seen.has(r.project_id)) rowError = "Duplicate project_id in file";
          else seen.add(r.project_id);
          return { ...r, _error: rowError, _status: rowError ? "skipped" : "pending" };
        });

        setRows(withMeta);
      } catch {
        setParseError("Could not parse file. Please use the provided Excel template.");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleUpload = async () => {
    const toProcess = rows.filter((r) => r._status === "pending");
    if (!toProcess.length) return;

    setUploading(true); setResults([]);
    const live = rows.map((r) => ({ ...r }));

    for (let i = 0; i < live.length; i++) {
      const row = live[i];
      if (row._status !== "pending") continue;

      const locId = locMap[row.location_name.toLowerCase()] || null;

      try {
        await createProject({
          project_id:          row.project_id,
          project_name:        row.project_name,
          project_description: row.project_description,
          location_id:         locId,
        });
        live[i] = { ...row, _status: "success", _error: "" };
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.project_id?.[0] ||
          err?.response?.data?.message ||
          "Unknown error";
        live[i] = { ...row, _status: "error", _error: msg };
      }

      setResults([...live]);
    }

    setUploading(false); setDone(true);
  };

  const displayRows   = results.length ? results : rows;
  const pendingCount  = rows.filter((r) => r._status === "pending").length;
  const successCount  = results.filter((r) => r._status === "success").length;
  const errorCount    = results.filter((r) => r._status === "error").length;
  const skippedCount  = results.filter((r) => r._status === "skipped").length;

  return (
    <ManagerLayout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* HEADER */}
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 max-w-5xl mx-auto">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl sm:text-3xl">📊</span>
                </div>
                <div>
                  <h1 className="text-xl sm:text-3xl font-bold text-white">Bulk Create Projects</h1>
                  <p className="text-white/60 text-xs sm:text-sm">Upload an Excel file to create multiple projects at once</p>
                </div>
              </div>
              <Link to="/manager/create-project" className="w-full sm:w-auto">
                <button className="w-full sm:w-auto px-5 py-2.5 rounded-xl border-2 border-white/30 text-white/80 hover:bg-white/10 text-sm font-medium transition-all">
                  ← Single Create
                </button>
              </Link>
            </div>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6">

            {/* STEP 1 – Download template */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <h2 className="text-white font-bold text-base">Download Template</h2>
                  </div>
                  <p className="text-white/50 text-xs sm:text-sm ml-8">
                    Get the Excel template. <code className="text-red-400 text-xs">location</code> is <strong className="text-red-400">required</strong> — use exact location name from system.
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                  onClick={handleDownloadTemplate}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-semibold text-sm shadow-lg w-full sm:w-auto flex-shrink-0"
                >
                  ⬇ Download Template
                </motion.button>
              </div>

              <div className="mt-4 ml-0 sm:ml-8 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4">
                {[
                  { col: "project_id",         req: true, hint: "Unique ID · max 20 chars · e.g. PROJ-001" },
                  { col: "project_name",        req: true, hint: "Human-readable name · max 100 chars" },
                  { col: "project_description", req: true, hint: "Full description, any length" },
                  { col: "location",            req: true, hint: "Must match exact location name in system" },
                ].map(({ col, req, hint }) => (
                  <div key={col} className="bg-white/5 rounded-xl p-2.5 sm:p-3 border border-white/10">
                    <div className="flex items-center gap-1.5 mb-1">
                      <code className={`text-xs font-bold ${col === "location" ? "text-red-400" : "text-violet-300"}`}>{col}</code>
                      {req && <span className="text-red-400 text-xs">★</span>}
                    </div>
                    <p className="text-white/40 text-xs">{hint}</p>
                  </div>
                ))}
              </div>

              {locations.length > 0 && (
                <div className="mt-3 ml-0 sm:ml-8 bg-white/5 rounded-xl p-3 border border-white/10">
                  <p className="text-white/50 text-xs font-semibold mb-1">Available locations (copy exact name):</p>
                  <p className="text-violet-300 text-xs break-words">{locations.map((l) => l.name).join(" · ")}</p>
                </div>
              )}
              {locations.length === 0 && (
                <div className="mt-3 ml-0 sm:ml-8 bg-red-500/10 rounded-xl p-3 border border-red-400/20">
                  <p className="text-red-400 text-xs">⚠ No project locations exist yet. Create locations first before bulk uploading.</p>
                </div>
              )}
            </motion.div>

            {/* STEP 2 – Upload file */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <h2 className="text-white font-bold text-base">Upload & Preview</h2>
              </div>

              <label
                className="border-2 border-dashed border-white/25 hover:border-violet-400/60 rounded-xl p-6 sm:p-8 text-center cursor-pointer transition-all hover:bg-violet-500/5 block"
              >
                <div className="text-4xl mb-2">📂</div>
                <p className="text-white/70 font-medium text-sm">Click to choose file</p>
                <p className="text-white/40 text-xs mt-1">.xlsx or .csv · max 500 rows</p>
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
              </label>

              {parseError && (
                <div className="mt-4 bg-red-500/15 border border-red-400/30 rounded-xl px-4 py-3 text-red-300 text-sm">
                  ⚠ {parseError}
                </div>
              )}

              <AnimatePresence>
                {displayRows.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5">

                    {done && (
                      <div className="flex gap-3 mb-4 flex-wrap">
                        <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-300 text-xs font-bold">{successCount} created</span>
                        {errorCount   > 0 && <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-300 text-xs font-bold">{errorCount} failed</span>}
                        {skippedCount > 0 && <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 text-xs font-bold">{skippedCount} skipped</span>}
                      </div>
                    )}

                    {/* Desktop table */}
                    <div className="overflow-x-auto rounded-xl border border-white/15 hidden sm:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-white/10 text-white/70 text-xs">
                            <th className="px-3 py-2 text-left font-semibold">#</th>
                            <th className="px-3 py-2 text-left font-semibold">project_id</th>
                            <th className="px-3 py-2 text-left font-semibold">project_name</th>
                            <th className="px-3 py-2 text-left font-semibold max-w-xs">project_description</th>
                            <th className="px-3 py-2 text-left font-semibold">location <span className="text-red-400">★</span></th>
                            <th className="px-3 py-2 text-left font-semibold">Status</th>
                            <th className="px-3 py-2 text-left font-semibold">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayRows.map((row, i) => (
                            <tr key={i} className={`border-t border-white/10 ${
                              row._status === "error"   ? "bg-red-500/5"   :
                              row._status === "success" ? "bg-green-500/5" :
                              row._status === "skipped" ? "bg-yellow-500/5": ""
                            }`}>
                              <td className="px-3 py-2 text-white/40 text-xs">{row._idx}</td>
                              <td className="px-3 py-2 text-white font-mono text-xs">{row.project_id || <span className="text-red-400">—</span>}</td>
                              <td className="px-3 py-2 text-white/80 text-xs">{row.project_name || <span className="text-red-400">—</span>}</td>
                              <td className="px-3 py-2 text-white/50 text-xs max-w-xs truncate">{row.project_description || <span className="text-red-400">—</span>}</td>
                              <td className="px-3 py-2 text-xs">
                                {!row.location_name
                                  ? <span className="text-red-400 font-semibold">Missing ★</span>
                                  : locMap[row.location_name.toLowerCase()]
                                    ? <span className="text-green-400">{row.location_name}</span>
                                    : <span className="text-red-400">{row.location_name} ✗</span>
                                }
                              </td>
                              <td className="px-3 py-2"><StatusBadge status={uploading && row._status === "pending" ? "pending" : row._status} /></td>
                              <td className="px-3 py-2 text-red-300 text-xs">{row._error || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile cards for preview rows */}
                    <div className="sm:hidden space-y-2">
                      {displayRows.map((row, i) => (
                        <div key={i} className={`rounded-xl border p-3 text-xs ${
                          row._status === "error"   ? "bg-red-500/5 border-red-400/20"   :
                          row._status === "success" ? "bg-green-500/5 border-green-400/20" :
                          row._status === "skipped" ? "bg-yellow-500/5 border-yellow-400/20":
                          "bg-white/5 border-white/10"
                        }`}>
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div>
                              <p className="text-white font-mono font-semibold">{row.project_id || <span className="text-red-400">—</span>}</p>
                              <p className="text-white/70">{row.project_name || "—"}</p>
                            </div>
                            <StatusBadge status={uploading && row._status === "pending" ? "pending" : row._status} />
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {!row.location_name
                              ? <span className="text-red-400 font-semibold">Missing location ★</span>
                              : locMap[row.location_name.toLowerCase()]
                                ? <span className="text-green-400">📍 {row.location_name}</span>
                                : <span className="text-red-400">📍 {row.location_name} ✗</span>
                            }
                          </div>
                          {row._error && <p className="text-red-300 mt-1">{row._error}</p>}
                        </div>
                      ))}
                    </div>

                    <p className="text-white/40 text-xs mt-2">
                      {displayRows.length} row{displayRows.length !== 1 ? "s" : ""} detected · {pendingCount} ready to upload
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* STEP 3 – Confirm & Upload */}
            {rows.length > 0 && !done && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <h2 className="text-white font-bold text-base">Confirm Upload</h2>
                  </div>
                  <p className="text-white/50 text-xs sm:text-sm ml-8 mt-0.5">
                    {pendingCount} project{pendingCount !== 1 ? "s" : ""} will be created.
                    {rows.length - pendingCount > 0 && ` ${rows.length - pendingCount} skipped.`}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                  onClick={handleUpload}
                  disabled={uploading || pendingCount === 0}
                  className="flex items-center justify-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-bold shadow-lg disabled:opacity-50 text-sm w-full sm:w-auto"
                >
                  {uploading ? (
                    <><span className="animate-spin">⟳</span> Uploading...</>
                  ) : (
                    <>🚀 Upload {pendingCount} Project{pendingCount !== 1 ? "s" : ""}</>
                  )}
                </motion.button>
              </motion.div>
            )}

            {/* Done state */}
            {done && (
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-green-500/10 border border-green-400/30 rounded-2xl p-4 sm:p-5">
                <div>
                  <p className="text-green-300 font-bold text-base">Upload Complete!</p>
                  <p className="text-white/50 text-sm mt-0.5">{successCount} created · {errorCount} failed · {skippedCount} skipped</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => { setRows([]); setResults([]); setDone(false); setParseError(""); }}
                    className="px-5 py-2.5 rounded-xl border-2 border-white/30 text-white text-sm hover:bg-white/10 transition-all"
                  >
                    Upload Another
                  </button>
                  <button
                    onClick={() => navigate("/manager/projects")}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-bold text-sm"
                  >
                    View Projects →
                  </button>
                </div>
              </motion.div>
            )}

          </div>
        </div>
      </div>
    </ManagerLayout>
  );
}